import React, { useEffect, useState } from "react";
import { Container, Row, Col, Modal, Button, Form, Breadcrumb } from "react-bootstrap";
import { useAppContext } from "../../context/app-context";
import { useParams, useNavigate } from "react-router-dom";
import { useAcubeService } from "../../service/acube-api.hook";
import format from "date-fns/format";
import { CopyToClipboard } from "react-copy-to-clipboard";
import SkeletonScreen from "../../layouts/skeleton";

import {
  FacebookShareButton,
  TwitterShareButton,
  LinkedinShareButton,
  EmailShareButton,
  FacebookIcon,
  TwitterIcon,
  LinkedinIcon,
  EmailIcon,
} from "react-share";

// ---------- Assets ----------
const like =
  "https://acube-s3.s3.us-east-1.amazonaws.com/Acube-portfolio/Images/blogs/thumbs-up-unfilled.svg";
const likeFilled =
  "https://acube-s3.s3.us-east-1.amazonaws.com/Acube-portfolio/Images/blogs/thumbs-up-filled.svg";
const share =
  "https://acube-s3.s3.us-east-1.amazonaws.com/Acube-portfolio/Images/blogs/share-icon.svg";
const copyLink =
  "https://acube-s3.s3.us-east-1.amazonaws.com/Acube-portfolio/Images/blogs/link.svg";
const copySuccess =
  "https://acube-s3.s3.us-east-1.amazonaws.com/Acube-portfolio/Images/blogs/copied.svg";

const APP_DOMAIN = process.env.REACT_APP_DOMAIN;

const CategoryPage = () => {
  const [blogData, setBlogData] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [currentBlog, setCurrentBlog] = useState(null);
  const [isCopied, setIsCopied] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { ipAddress } = useAppContext();
  const { category } = useParams();
  const decodedCategory = decodeURIComponent(category.replace(/-/g, " "));
  const { getProxy, postProxy } = useAcubeService();
  const navigate = useNavigate();

  // ---------- Fetch Blogs ----------
  const fetchBlogs = async () => {
    try {
      setIsLoading(true);
      const result = await getProxy(`blog/category/${decodedCategory}/${ipAddress}`);
      setBlogData(result.data || []);
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (ipAddress && category) fetchBlogs();
  }, [ipAddress, category]);

  useEffect(() => {
    setIsLoading(true);
  }, []);

  // ---------- Helpers ----------
  const formatDate = (dateString) => format(new Date(dateString), "d MMMM yyyy");
  const shareURL = currentBlog ? `${APP_DOMAIN}/blogs/${currentBlog.slug}` : "";

  const handleLikeClick = async (blog) => {
    try {
      const alreadyLiked = blog.likes && blog.likes.length > 0;

      await postProxy(`blog/like`, { blogUUID: blog.blog_uuid, ip: ipAddress });

      setBlogData((prev) => {
        const updated = { ...prev };
        ["recentBlogs", "mostViewedBlogs", "popularBlogs"].forEach((key) => {
          if (Array.isArray(updated[key])) {
            updated[key] = updated[key].map((b) => {
              if (b.blog_uuid === blog.blog_uuid) {
                return alreadyLiked
                  ? { ...b, likesAdded: Math.max(0, b.likesAdded - 1), likes: [] }
                  : { ...b, likesAdded: b.likesAdded + 1, likes: [{}] };
              }
              return b;
            });
          }
        });
        return updated;
      });
    } catch (err) {
      console.log("Error toggling like:", err);
    }
  };

  const handleShareClick = (blog, e) => {
    e.stopPropagation();
    setCurrentBlog(blog);
    setShowModal(true);
    setIsCopied(false);
  };

  const handleBlogClick = (blog) => navigate(`/blogs/${blog.slug}`);
  const handleCopy = () => setIsCopied(true);

  // ---------- Render ----------
  return (
    <section className="search-results">
      <Container fluid className="py-3">
        <Breadcrumb className="mb-3">
          <Breadcrumb.Item onClick={() => navigate("/blogs")}>Blogs</Breadcrumb.Item>
          <Breadcrumb.Item active>{decodedCategory}</Breadcrumb.Item>
        </Breadcrumb>
        <h2 className="ft-wt-700 mb-4">Search Results for "{decodedCategory}"</h2>

        <Row className="blog-cards">
          {isLoading ? (
            <SkeletonScreen count={6} />
          ) : blogData.length ? (
            blogData.map((blog) => (
              <Col
                xs="12"
                md="6"
                lg="4"
                key={blog.id}
                className="mb-4 blog-card cursor-pointer"
                onClick={() => handleBlogClick(blog)}
              >
                <img src={blog.template_image} alt={blog.title} className="blog-thumbnail" />
                <div className="blog-card-info">
                  <div className="blog-meta">
                    <span className="author">{blog.service_category}</span>
                    <span className="date">{formatDate(blog.created_at)}</span>
                  </div>
                  <h3 className="blog-card-title">{blog.title}</h3>
                  <p className="blog-content">{blog.template_content || "No preview available."}</p>
                  <div className="blog-actions d-flex justify-content-between">
                    <div className="d-flex align-items-center gap-2">
                      <img
                        src={blog?.likes?.length ? likeFilled : like}
                        alt="thumbs-up"
                        aria-label="Like this blog"
                        onClick={(e) => handleLikeClick(blog, e)}
                      />
                      <img
                        src={share}
                        alt="share"
                        aria-label="Share this blog"
                        className="cursor-pointer"
                        onClick={(e) => handleShareClick(blog, e)}
                      />
                    </div>
                    <div className="d-flex align-items-center gap-2">
                      <h6 className="mb-0">{blog.likesAdded} likes</h6>
                      <h6 className="mb-0">{blog.views} views</h6>
                    </div>
                  </div>
                </div>
              </Col>
            ))
          ) : (
            <Col>
              <p>No results found for "{decodedCategory}".</p>
            </Col>
          )}
        </Row>
      </Container>

      {/* ---------- Share Modal ---------- */}
      <Modal show={showModal} onHide={() => setShowModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title className="ft-wt-700">Share This Blog</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3 text-center">
              <Form.Label>Share to</Form.Label>
              <div className="d-flex gap-2 justify-content-center">
                <FacebookShareButton url={shareURL}>
                  <FacebookIcon size={48} round />
                </FacebookShareButton>
                <TwitterShareButton url={shareURL}>
                  <TwitterIcon size={48} round />
                </TwitterShareButton>
                <LinkedinShareButton url={shareURL}>
                  <LinkedinIcon size={48} round />
                </LinkedinShareButton>
                <EmailShareButton url={shareURL}>
                  <EmailIcon size={48} round />
                </EmailShareButton>
              </div>
            </Form.Group>

            <Form.Group className="mb-3 position-relative">
              <Form.Label>Or copy the link</Form.Label>
              <div className="d-flex align-items-center">
                <Form.Control type="text" value={shareURL} readOnly />
                <CopyToClipboard text={shareURL} onCopy={handleCopy}>
                  <Button className="ms-2 p-0 copy-btn" variant="link">
                    <img
                      src={isCopied ? copySuccess : copyLink}
                      alt={isCopied ? "copied" : "copy"}
                      className="cursor-pointer"
                    />
                  </Button>
                </CopyToClipboard>
              </div>
              {isCopied && <div className="link-success-msg mt-2">Link copied successfully!</div>}
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="link"
            className="ft-wt-700 text-primary text-decoration-none"
            onClick={() => setShowModal(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default CategoryPage;
